import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import {
  DayFilter,
  RoleFilter,
  filterByRole,
  filterDailyRoutines,
} from "./filter";
import { useTeamScheduleAdminScreenListData_Routine$key } from "./__generated__/useTeamScheduleAdminScreenListData_Routine.graphql";
import { DateTime } from "luxon";

function timeInMinutes(time?: { hour: number; minute: number } | null) {
  return (time?.hour ?? 0) * 60 + (time?.minute ?? 0);
}

export function useTeamScheduleAdminScreenListData(props: {
  routines: useTeamScheduleAdminScreenListData_Routine$key;
  selectedDaysFilter: DayFilter;
  selectedRoleFilter: RoleFilter;
}) {
  const routines = useFragment(
    graphql`
      fragment useTeamScheduleAdminScreenListData_Routine on Routine
      @relay(plural: true) {
        id
        title
        ...TeamScheduleAdminItem_routine
        assignedRoles {
          role {
            id
          }
        }
        currentSchedule {
          __typename
          ... on RoutineScheduleDaily {
            weekdays {
              weekday
              startTime {
                hour
                minute
              }
            }
            isAllDay
          }
          ... on RoutineScheduleWeekly {
            startWeekday
          }
          ... on RoutineScheduleMonthly {
            startDay
            endDay
          }
        }
      }
    `,
    props.routines
  );

  type ItemType = (typeof routines)[number];

  const roleFilteredRoutines = useMemo(
    () =>
      routines?.filter((routine) =>
        filterByRole(props.selectedRoleFilter, routine.assignedRoles)
      ) ?? [],
    [routines, props.selectedRoleFilter]
  );

  const categorizeRoutines = (items: readonly ItemType[]) => {
    const initial = {
      monthly: [] as ItemType[],
      weekly: [] as ItemType[],
      allDay: [] as ItemType[],
      timeOfDay: [] as ItemType[],
    } as const;
    return (
      items.reduce((acc, routine) => {
        const schedule = routine.currentSchedule;
        switch (schedule?.__typename) {
          case "RoutineScheduleDaily":
            return {
              ...acc,
              ...(schedule.isAllDay
                ? { allDay: [...acc.allDay, routine] }
                : {
                    timeOfDay: [...acc.timeOfDay, routine],
                  }),
            };
          case "RoutineScheduleWeekly":
            return { ...acc, weekly: [...acc.weekly, routine] };
          case "RoutineScheduleMonthly":
            return { ...acc, monthly: [...acc.monthly, routine] };
          default:
            return acc;
        }
      }, initial) ?? initial
    );
  };

  const sortedRoutines = useMemo(
    () => categorizeRoutines(roleFilteredRoutines),
    [roleFilteredRoutines]
  );

  const unfilteredCats = categorizeRoutines(routines ?? []);
  const unfilteredCounts = {
    dailyTotalNum:
      unfilteredCats.timeOfDay.length + unfilteredCats.allDay.length,
    weeklyTotalNum: unfilteredCats.weekly.length,
    monthlyTotalNum: unfilteredCats.monthly.length,
  };

  const timeOfDay = useMemo(() => {
    const sortedTimeOfDay = sortedRoutines.timeOfDay
      .filter((routine) => {
        const schedule = routine.currentSchedule;
        if (schedule?.__typename === "RoutineScheduleDaily") {
          return filterDailyRoutines(
            props.selectedDaysFilter,
            schedule.weekdays.map(({ weekday }) => weekday)
          );
        }
        return false;
      })
      .sort((a, b) => {
        if (
          a.currentSchedule?.__typename === "RoutineScheduleDaily" &&
          b.currentSchedule?.__typename === "RoutineScheduleDaily"
        ) {
          return (
            Math.min(
              ...a.currentSchedule.weekdays.map(({ startTime }) =>
                timeInMinutes(startTime)
              )
            ) -
            Math.min(
              ...b.currentSchedule.weekdays.map(({ startTime }) =>
                timeInMinutes(startTime)
              )
            )
          );
        }
        throw new Error("Should not happen");
      });

    const startTimeBetween = (
      first?: (typeof sortedTimeOfDay)[number],
      second?: (typeof sortedTimeOfDay)[number]
    ) => {
      if (
        first?.currentSchedule?.__typename === "RoutineScheduleDaily" &&
        second?.currentSchedule?.__typename === "RoutineScheduleDaily"
      ) {
        if (
          !first.currentSchedule.weekdays[0]?.startTime ||
          !second.currentSchedule.weekdays[0]?.startTime
        ) {
          throw new Error("Should not happen");
        }
        const firstStartDate = DateTime.fromObject(
          first.currentSchedule.weekdays[0].startTime
        );
        const secondStartDate = DateTime.fromObject(
          second.currentSchedule.weekdays[0].startTime
        );
        const diffMinutes = Math.abs(
          firstStartDate.diff(secondStartDate).as("minutes")
        );

        const { hour, minute } = secondStartDate.minus({
          minutes: Math.max(0, Math.round(Math.min(15, diffMinutes / 2))),
        });
        return { hour, minute };
      }
      if (
        first?.currentSchedule?.__typename === "RoutineScheduleDaily" &&
        first.currentSchedule.weekdays[0]?.startTime
      ) {
        const { hour, minute } = DateTime.fromObject(
          first.currentSchedule.weekdays[0].startTime
        )
          .plus({ minutes: 15 })
          .toObject();
        return {
          hour: hour!,
          minute: minute!,
        };
      }
      if (
        second?.currentSchedule?.__typename === "RoutineScheduleDaily" &&
        second.currentSchedule.weekdays[0]?.startTime
      ) {
        const { hour, minute } = DateTime.fromObject(
          second.currentSchedule.weekdays[0].startTime
        )
          .minus({ minutes: 15 })
          .toObject();
        return {
          hour: hour!,
          minute: minute!,
        };
      }
      return {
        hour: 12,
        minute: 0,
      };
    };

    return sortedTimeOfDay.flatMap((routine, index) => [
      {
        itemType: "ROUTINE_TIME_OF_DAY" as const,
        routine,
        index,
        id: routine.id,
        startTimeAddBefore: startTimeBetween(
          index === 0 ? undefined : sortedTimeOfDay[index - 1],
          routine
        ),
        startTimeAddAfter: startTimeBetween(
          routine,
          index === sortedTimeOfDay.length - 1
            ? undefined
            : sortedTimeOfDay[index + 1]
        ),
        isFirst: index === 0,
        isLast: sortedTimeOfDay.length - 1 === index,
      },
    ]);
  }, [props.selectedDaysFilter, sortedRoutines.timeOfDay]);

  const monthly = useMemo(
    () =>
      sortedRoutines.monthly.sort((a, b) => {
        if (
          a.currentSchedule?.__typename !== "RoutineScheduleMonthly" ||
          b.currentSchedule?.__typename !== "RoutineScheduleMonthly"
        ) {
          throw new Error("Should not happen");
        }
        if (a.currentSchedule.startDay !== b.currentSchedule.startDay) {
          return a.currentSchedule.startDay - b.currentSchedule.startDay;
        }
        if (a.currentSchedule.endDay !== b.currentSchedule.endDay) {
          return (
            (a.currentSchedule.endDay ?? Infinity) -
            (b.currentSchedule.endDay ?? Infinity)
          );
        }
        return 0;
      }),
    [sortedRoutines.monthly]
  );

  const weekly = useMemo(
    () =>
      sortedRoutines.weekly.sort((a, b) => {
        if (
          a.currentSchedule?.__typename !== "RoutineScheduleWeekly" ||
          b.currentSchedule?.__typename !== "RoutineScheduleWeekly"
        ) {
          throw new Error("Should not happen");
        }
        return (
          (a.currentSchedule.startWeekday ?? 0) -
          (b.currentSchedule.startWeekday ?? 0)
        );
      }),
    [sortedRoutines.weekly]
  );

  const allDay = useMemo(
    () =>
      sortedRoutines.allDay.sort((a, b) => {
        if (
          a.currentSchedule?.__typename !== "RoutineScheduleDaily" ||
          b.currentSchedule?.__typename !== "RoutineScheduleDaily"
        ) {
          throw new Error("Should not happen");
        }
        return a.title > b.title ? 1 : -1;
      }),
    [sortedRoutines.allDay]
  );

  return {
    monthly: monthly.map((routine, index) => ({
      routine,
      id: routine.id,
      itemType: "ROUTINE_MONTHLY" as const,
      isLast: monthly.length - 1 === index,
      isFirst: index === 0,
    })),
    weekly: weekly.map((routine, index) => ({
      routine,
      id: routine.id,
      itemType: "ROUTINE_WEEKLY" as const,
      isLast: weekly.length - 1 === index,
      isFirst: index === 0,
    })),
    unfilteredCounts,
    dailyTotalNum:
      sortedRoutines.timeOfDay.length + sortedRoutines.allDay.length,
    daily: [
      ...allDay.map((routine, index) => {
        return {
          routine,
          id: routine.id,
          itemType: "ROUTINE_ALL_DAY" as const,
          isLast: monthly.length - 1 === index,
          isFirst: index === 0,
        };
      }),
      ...timeOfDay,
    ],
  };
}
